/**
 * Referral & Approval Dates (OR-1244) — right-column card with a calendar
 * icon (latest Figma). Referral Date + Approval Date sit side by side. Both
 * feed the Quote save (referral_date / approval_date).
 *
 * `DateTextField` masks MM/DD/YYYY and blocks non-digits; a light
 * real-date check flags impossible dates on blur.
 */
import { useState } from "react";
import { Calendar } from "@untitledui/icons";
import { DateTextField } from "../../../../components/date-text-field";
import { FormGroup } from "../../../../components/form-group";

/** "MM/DD/YYYY" → is it a real calendar date? Partials are invalid. */
function isValidDate(value: string): boolean {
    const m = value.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
    if (!m) return false;
    const [, mm, dd, yyyy] = m.map(Number);
    const d = new Date(yyyy, mm - 1, dd);
    return d.getFullYear() === yyyy && d.getMonth() === mm - 1 && d.getDate() === dd;
}

interface ReferralApprovalDatesCardProps {
    referralDate: string;
    approvalDate: string;
    onReferralDateChange: (value: string) => void;
    onApprovalDateChange: (value: string) => void;
}

export function ReferralApprovalDatesCard({
    referralDate,
    approvalDate,
    onReferralDateChange,
    onApprovalDateChange,
}: ReferralApprovalDatesCardProps) {
    return (
        <FormGroup title="Referral & Approval Dates" icon={Calendar} data-autotest="premium-selection-referral-approval-dates-card">
            {/* Side by side per the approved layout (Andressa, 4-ago); stacks
              * on narrow screens since the card already sits in a column. */}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <DateRow
                    label="Referral Date"
                    value={referralDate}
                    onChange={onReferralDateChange}
                    dataAutotest="premium-selection-referral-approval-dates-referral-date-input"
                />
                <DateRow
                    label="Approval Date"
                    value={approvalDate}
                    onChange={onApprovalDateChange}
                    dataAutotest="premium-selection-referral-approval-dates-approval-date-input"
                />
            </div>
        </FormGroup>
    );
}

function DateRow({
    label,
    value,
    onChange,
    dataAutotest,
}: {
    label: string;
    value: string;
    onChange: (value: string) => void;
    dataAutotest?: string;
}) {
    const [touched, setTouched] = useState(false);
    const invalid = touched && value !== "" && !isValidDate(value);
    return (
        <DateTextField
            label={label}
            defaultValue={value}
            onChange={onChange}
            onBlur={() => setTouched(true)}
            isInvalid={invalid}
            hint={invalid ? "Enter a valid date (MM/DD/YYYY)." : undefined}
            data-autotest={dataAutotest}
        />
    );
}
